import {Button as PepsicoDsButtonWithIcon} from '@pepsico-ds/ui'
import PropTypes from 'prop-types'

/**
 * Button component that renders a PepsicoDsButtonWithIcon with properties from uiSchema.
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.handleButton - The function to handle button click events.
 * @param {string} props.iconLeading - The icon of the button.
 * @param {Object} props.uiSchema - The schema object containing button properties.
 * @param {string} props.uiSchema.className - The CSS class name for the button.
 * @param {string} props.uiSchema.size - The size of the button.
 * @param {string} props.uiSchema.title - The text to display on the button.
 * @param {string} props.uiSchema.variant - The variant style of the button.
 * @param {boolean} props.uiSchema.isLoading - The loading state of the button.
 * @param {ReactNode} [props.uiSchema.iconTrailing] - An optional icon to display at the end of the button.
 * @param {boolean} [props.uiSchema.isDark] - Optional boolean to indicate dark mode styling for the button.
 * @param {boolean} [props.uiSchema.disabled] -  props.disabled - To disable the button.
 *
 * @returns {JSX.Element} The rendered PepsicoDsButtonWithIcon component.
 */
const ButtonWithIcon = ({id, handleButton, uiSchema = {}, children, iconLeading = ''}) => {
  const {className, size, title, variant, isLoading, iconTrailing, isDark = false, disabled} = uiSchema

  return (
    <PepsicoDsButtonWithIcon
      id={`button-with-icon` + (id ? `_${id}` : '')}
      data-testid={`button-with-icon` + (id ? `_${id}` : '')}
      text={title}
      onClick={handleButton}
      variant={variant}
      size={size}
      className={className}
      isLoading={isLoading}
      iconTrailing={iconTrailing}
      isDark={isDark}
      disabled={disabled}
      iconLeading={iconLeading}
    >
      {children}
    </PepsicoDsButtonWithIcon>
  )
}

ButtonWithIcon.propTypes = {
  id: PropTypes.string,
  handleButton: PropTypes.func,
  uiSchema: PropTypes.shape({
    className: PropTypes.string,
    size: PropTypes.string,
    title: PropTypes.string,
    variant: PropTypes.string,
    isLoading: PropTypes.bool,
    iconTrailing: PropTypes.node,
    isDark: PropTypes.bool,
    disabled: PropTypes.bool,
  }),
  children: PropTypes.node,
  iconLeading: PropTypes.string,
}

export default ButtonWithIcon
